// Eventos en vivo de la oficina v2 (diseño §8). Se monta una vez, en la raíz autenticada.
import { useEffect } from 'react';
import { getSocket } from '../../lib/socket';
import { recargarChatAbierto, recargarExpedienteSiAbierto, useOficinaStore } from './store';
import { alCambioPelotas, type CambioPelotas } from './pelotas/store';
import type { ContratadoDTO } from './compartido/vocabulario';

/** Tras volver a la app o reconectar, otra puesta al día en menos de esto sobra (revisión 09-10: se hacía dos veces). */
export const ENTRE_PUESTAS_AL_DIA_MS = 5_000;
/** Volver a la app tras menos de esto oculta no recarga nada (cambiar de pestaña un momento). */
export const OCULTA_MINIMO_MS = 30_000;
let ultimaPuestaAlDia = 0;

/** La oficina (si está en pantalla) y el chat abierto vuelven a pedirse enteros: lo de durante el corte no llegó. */
function ponerseAlDia(hidratar: () => Promise<void>): void {
  const ahora = Date.now();
  if (ahora - ultimaPuestaAlDia < ENTRE_PUESTAS_AL_DIA_MS) return;
  ultimaPuestaAlDia = ahora;
  if (useOficinaStore.getState().abierta) void hidratar();
  // 29-09: y el chat abierto (su conversación y sus tarjetas), que se quedaba con lo de antes del corte.
  recargarChatAbierto();
}

export function useSocketOficina(): void {
  useEffect(() => {
    const socket = getSocket();
    const { hidratar, actualizarContratado } = useOficinaStore.getState();
    const alConectar = () => ponerseAlDia(hidratar);
    // 09-10: al volver a la app (iPhone en segundo plano, otra pestaña) se pone al día sin esperar al socket, que en
    // iOS puede tardar en notar que la conexión murió mientras la app dormía.
    let ocultaDesde: number | null = null;
    const alVolver = () => {
      if (document.visibilityState === 'hidden') {
        ocultaDesde = Date.now();
        return;
      }
      const rato = ocultaDesde === null ? 0 : Date.now() - ocultaDesde;
      ocultaDesde = null;
      if (rato >= OCULTA_MINIMO_MS) ponerseAlDia(hidratar);
    };
    document.addEventListener('visibilitychange', alVolver);
    const alContratado = (p: { contratado: ContratadoDTO }) => {
      actualizarContratado(p.contratado);
      recargarExpedienteSiAbierto(p.contratado.agentId);
    };
    const alHistorial = (p: { agentId: string }) => recargarExpedienteSiAbierto(p.agentId);
    // Una sala cambió de nombre/color o se quitó: se recarga la oficina (si está en pantalla).
    const alSalas = () => {
      if (useOficinaStore.getState().abierta) void hidratar();
    };
    // 01-11 (pelotas v7): algo cambió en los contratos con Santi (la lista, un hilo, la tarjeta de un chat, General).
    const alPelotas = (p: CambioPelotas) => alCambioPelotas(p);
    socket.on('oficina:salas_cambiadas', alSalas);
    socket.on('connect', alConectar);
    socket.on('oficina:contratado', alContratado);
    socket.on('oficina:contratado_actualizado', alContratado);
    socket.on('oficina:historial', alHistorial);
    socket.on('oficina:pelotas', alPelotas);
    return () => {
      document.removeEventListener('visibilitychange', alVolver);
      socket.off('connect', alConectar);
      socket.off('oficina:contratado', alContratado);
      socket.off('oficina:contratado_actualizado', alContratado);
      socket.off('oficina:historial', alHistorial);
      socket.off('oficina:salas_cambiadas', alSalas);
      socket.off('oficina:pelotas', alPelotas);
    };
  }, []);
}
